import { Box, Button, Paper, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowRight } from "@tabler/icons-react";
import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  PAGE_CTA_DEFAULTS,
  resolvePageCtaBoxSize,
  type PageCtaBoxSize,
  type PageCtaLayout,
  type PageCtaPublicItem,
} from "@shared/pageCtaSlots";
import { PROFILE_CARD_PAPER_STYLE } from "@/shared/styles/profileSectionHeader";

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function PageCtaFixedBox({
  box,
  children,
  mb,
}: {
  box: PageCtaBoxSize;
  children: ReactNode;
  mb?: string | number;
}) {
  return (
    <Box
      w="100%"
      maw={box.fullWidth ? undefined : box.widthPx}
      mx={box.fullWidth ? 0 : "auto"}
      mb={mb}
      style={{
        aspectRatio: `${box.widthPx} / ${box.heightPx}`,
        overflow: "hidden",
        borderRadius: "var(--mantine-radius-md)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box w="100%" h="100%" pos="relative">
        {children}
      </Box>
    </Box>
  );
}

export function ManagedCtaCard({
  item,
  layout,
  disableLink = false,
  onPlaceButton,
  forceArtwork,
  fillContainer = false,
  boxSize,
}: {
  item: PageCtaPublicItem;
  layout?: PageCtaLayout;
  disableLink?: boolean;
  onPlaceButton?: (xPct: number, yPct: number) => void;
  forceArtwork?: "desktop" | "mobile";
  fillContainer?: boolean;
  boxSize?: PageCtaBoxSize;
}) {
  const resolvedLayout = layout ?? item.layout;
  const buttonTextColor = item.buttonTextColor || PAGE_CTA_DEFAULTS.buttonTextColor;
  const buttonBackgroundColor = item.buttonBackgroundColor || PAGE_CTA_DEFAULTS.buttonBackgroundColor;
  const buttonFontSize = item.buttonFontSize || PAGE_CTA_DEFAULTS.buttonFontSize;
  const isSidebar = resolvedLayout === "sidebar";
  const isCompanyProfile = item.pageKey === "COMPANY_PROFILE";
  const isNarrow = useMediaQuery("(max-width: 47.9375em)");
  const wantMobile =
    forceArtwork === "mobile" || (forceArtwork !== "desktop" && Boolean(isNarrow) && Boolean(item.mobileImageUrl));
  const imageUrl = wantMobile ? item.mobileImageUrl : item.imageUrl;
  const xPct = clampPct(
    wantMobile
      ? (item.mobileButtonXPct ?? item.buttonXPct ?? PAGE_CTA_DEFAULTS.buttonXPct)
      : (item.buttonXPct ?? PAGE_CTA_DEFAULTS.buttonXPct),
  );
  const yPct = clampPct(
    wantMobile
      ? (item.mobileButtonYPct ?? item.buttonYPct ?? PAGE_CTA_DEFAULTS.buttonYPct)
      : (item.buttonYPct ?? PAGE_CTA_DEFAULTS.buttonYPct),
  );
  const external = isExternalUrl(item.buttonUrl);
  const placing = Boolean(onPlaceButton);
  const resolvedBox = boxSize ?? resolvePageCtaBoxSize(item);
  const buttonRootStyles = {
    backgroundColor: buttonBackgroundColor,
    color: buttonTextColor,
    border: "none",
    fontSize: buttonFontSize,
    fontWeight: 700,
    maxWidth: "100%",
    width: item.buttonWidthPx ?? undefined,
    height: item.buttonHeightPx ?? undefined,
    minHeight: item.buttonHeightPx ?? undefined,
    pointerEvents: placing || disableLink ? ("none" as const) : undefined,
  };

  const button = disableLink || placing ? (
    <Button
      radius="xl"
      size={isSidebar || wantMobile ? "sm" : "md"}
      rightSection={<IconArrowRight size={isSidebar || wantMobile ? 14 : 16} />}
      styles={{ root: buttonRootStyles }}
    >
      {item.buttonLabel}
    </Button>
  ) : (
    <Button
      component={external ? "a" : Link}
      {...(external
        ? { href: item.buttonUrl, target: "_blank", rel: "noopener noreferrer" }
        : { to: item.buttonUrl })}
      radius="xl"
      size={isSidebar || wantMobile ? "sm" : "md"}
      rightSection={<IconArrowRight size={isSidebar || wantMobile ? 14 : 16} />}
      styles={{
        root: {
          ...buttonRootStyles,
          "&:hover": {
            backgroundColor: buttonBackgroundColor,
            filter: "brightness(0.95)",
          },
        },
      }}
    >
      {item.buttonLabel}
    </Button>
  );

  const handlePlace = placing
    ? (e: MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const nextX = clampPct(((e.clientX - rect.left) / rect.width) * 100);
        const nextY = clampPct(((e.clientY - rect.top) / rect.height) * 100);
        onPlaceButton?.(Math.round(nextX * 10) / 10, Math.round(nextY * 10) / 10);
      }
    : undefined;

  const overlayButton = (
    <div
      style={{
        position: "absolute",
        left: `${xPct}%`,
        top: `${yPct}%`,
        zIndex: 1,
        maxWidth: "100%",
      }}
    >
      {button}
    </div>
  );

  const art = (
    <Box
      onClick={handlePlace}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        cursor: placing ? "crosshair" : "default",
        backgroundColor: "#ffffff",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center",
            pointerEvents: "none",
          }}
        />
      ) : (
        <Text
          size="sm"
          c="dimmed"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: wantMobile ? 180 : 120,
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {wantMobile ? "Upload a mobile CTA image" : "Upload a CTA image"}
        </Text>
      )}
      {overlayButton}
    </Box>
  );

  const paper = isCompanyProfile ? (
    <Paper
      p="md"
      radius="lg"
      h="100%"
      style={{ ...PROFILE_CARD_PAPER_STYLE, minWidth: 0, maxWidth: "100%", height: "100%" }}
    >
      {art}
    </Paper>
  ) : (
    <Paper radius="md" p={0} h="100%" style={{ minWidth: 0, maxWidth: "100%", width: "100%", height: "100%", overflow: "hidden" }}>
      {art}
    </Paper>
  );

  if (fillContainer) return paper;

  return (
    <PageCtaFixedBox box={resolvedBox}>{paper}</PageCtaFixedBox>
  );
}
